<script setup lang="ts">
import { PlayerHandPanel, PlayerHandPanelAction } from '@/components/game/player-hand-panel/index.js';
import { PlayerHand } from '@/components/game/player-hand/index.js';
import { computed } from 'vue';
import { PButton } from '@pokerly/ui';
import { useSessionStore } from '@/stores/session';
import type { DraftVotingScalePreview } from './draft-voting-scale-preview.ts';

const props = withDefaults(
    defineProps<{
        draftVotingScale?: DraftVotingScalePreview | null;
    }>(),
    {
        draftVotingScale: null,
    },
);

const sessionStore = useSessionStore();

function handlePrimaryAction() {
    if (!sessionStore.canVote) {
        return;
    }
    if (sessionStore.isRoundClosed) {
        sessionStore.startRound();
        return;
    }

    void sessionStore.closeRound();
}

const isPrimaryActionDisabled = computed(
    () => !sessionStore.canVote || (!sessionStore.isRoundClosed && sessionStore.statistics.votesCasted === 0),
);
const primaryActionLabel = computed(() => (sessionStore.isRoundClosed ? 'Restart' : 'Reveal cards'));

const isObserver = computed(() => {
    const role = sessionStore.participants[0]?.role;
    if (!role) return true;
    return !['voter', undefined, null].includes(role);
});

const handValues = computed(() => {
    if (props.draftVotingScale && sessionStore.isSessionCreated) {
        return props.draftVotingScale.options;
    }
    return sessionStore.estimationOptions;
});
</script>

<template>
    <div class="w-full absolute left-0 bottom-0 z-10">
        <PlayerHandPanel>
            <PlayerHand
                v-if="!isObserver"
                :collapsed="sessionStore.isRoundClosed"
                :values="handValues"
                :modelValue="sessionStore.estimation"
                @update:modelValue="sessionStore.setEstimation"
            />
            <PlayerHandPanelAction
                :expected-votes="sessionStore.isSessionCreated ? undefined : sessionStore.voters.length"
                :total-votes="sessionStore.isSessionCreated ? undefined : sessionStore.statistics.votesCasted"
                :variant="isObserver ? 'icon' : 'default'"
            >
                <PButton
                    v-if="sessionStore.isSessionActive"
                    variant="secondary"
                    :disabled="isPrimaryActionDisabled"
                    @click="handlePrimaryAction"
                >
                    {{ primaryActionLabel }}
                </PButton>
            </PlayerHandPanelAction>
        </PlayerHandPanel>
    </div>
</template>
